import type {
  BodyRateMap,
  CreateRingPayload,
  CreateTariffPayload,
  DistanceTier,
  HariCode,
  ShiftCode,
  TariffConfig,
  TariffRing,
  TariffTipe,
} from '@/services/api/tariffs';

import { UJP_COPY } from '../copy';
import { toNum, todayIso } from '../format';

/**
 * Pure draft ↔ payload helpers for the `/ujp/tariffs` modals (CR-4b). The
 * page keeps every field as a STRING while typing (money digits, km text) and
 * converts once, here, at save. Validation mirrors the server DTO (§20.2):
 * tiers `maxKm > 0`, strictly increasing after sort, unique; body-override
 * keys non-empty and unique (case-insensitive); free points an integer ≥ 0.
 */

const v = UJP_COPY.tariffs.validation;

// ─────────────────────────────────────────────────────────────────────────────
// Rows
// ─────────────────────────────────────────────────────────────────────────────

/** One per-body override row: unit type text + money digits. */
export interface BodyRateRow {
  key: string;
  rate: string;
}

/** One DISTANCE_TIER row: km text (`"50"`, `"12,5"`) + money digits. */
export interface TierRow {
  maxKm: string;
  rate: string;
}

export const blankBodyRow = (): BodyRateRow => ({ key: '', rate: '0' });
export const blankTierRow = (): TierRow => ({ maxKm: '', rate: '0' });

export const bodyRowsFromMap = (map?: BodyRateMap | null): BodyRateRow[] =>
  Object.entries(map ?? {}).map(([key, rate]) => ({
    key,
    rate: String(Math.round(toNum(rate))),
  }));

/** Rows → wire map. Blank keys are dropped; an empty rate becomes `"0"`. */
export const bodyRowsToMap = (rows: BodyRateRow[]): BodyRateMap => {
  const out: BodyRateMap = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    out[key] = row.rate.trim() || '0';
  }
  return out;
};

/** Duplicate (case-insensitive, trimmed) or blank-but-priced keys are errors. */
export const validateBodyRows = (rows: BodyRateRow[]): string | null => {
  const seen = new Set<string>();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) {
      if (toNum(row.rate) > 0) return v.bodyKeyEmpty;
      continue;
    }
    const norm = key.toLowerCase();
    if (seen.has(norm)) return v.bodyKeyDuplicate;
    seen.add(norm);
  }
  return null;
};

/** `"12,5"` | `"50"` → 12.5 | 50; blank / garbage → NaN. */
export const parseKm = (raw: string): number => {
  const s = raw.trim().replace(',', '.');
  if (!s) return NaN;
  return Number(s);
};

/** Rows → tiers sorted ascending by `maxKm` (CR4b-D3). Invalid rows dropped. */
export const tierRowsToPayload = (rows: TierRow[]): DistanceTier[] =>
  rows
    .map((r) => ({ maxKm: parseKm(r.maxKm), rate: r.rate.trim() || '0' }))
    .filter((t) => Number.isFinite(t.maxKm) && t.maxKm > 0)
    .sort((a, b) => a.maxKm - b.maxKm);

/** At least one row; every `maxKm > 0`; strictly increasing after sort. */
export const validateTiers = (rows: TierRow[]): string | null => {
  if (rows.length === 0) return v.tiersEmpty;
  const kms = rows.map((r) => parseKm(r.maxKm));
  if (kms.some((k) => !Number.isFinite(k) || k <= 0)) return v.tierMaxKm;
  const sorted = kms.slice().sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] <= sorted[i - 1]) return v.tiersIncreasing;
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Config draft
// ─────────────────────────────────────────────────────────────────────────────

export interface SharesDraft {
  driver: string;
  helper: string;
  driverMulti: string;
  helperMulti: string;
}

export const blankShares = (): SharesDraft => ({
  driver: '0',
  helper: '0',
  driverMulti: '0',
  helperMulti: '0',
});

export interface ConfigDraft {
  tipe: TariffTipe;
  rateFlat: string;
  rateFlatMulti: string;
  rateFixed: string;
  asuransi: string;
  hargaReverse: string;
  ratesFlatPerBody: BodyRateRow[];
  ratesFlatPerBodyMulti: BodyRateRow[];
  shares: SharesDraft;
  hariOperasi: HariCode[];
  shiftOperasi: ShiftCode[];
  tagihPerRit: boolean;
  distanceTiers: TierRow[];
  multidropFreePoints: string;
  multidropExtraRate: string;
  catatan: string;
  berlakuMulai: string;
  /** `""` = open-ended (sent as null). */
  berlakuSampai: string;
}

export const blankConfig = (): ConfigDraft => ({
  tipe: 'PER_TRIP_FLAT',
  rateFlat: '0',
  rateFlatMulti: '0',
  rateFixed: '0',
  asuransi: '0',
  hargaReverse: '0',
  ratesFlatPerBody: [],
  ratesFlatPerBodyMulti: [],
  shares: blankShares(),
  hariOperasi: [],
  shiftOperasi: [],
  tagihPerRit: false,
  // One empty row so the DISTANCE_TIER editor never opens blank.
  distanceTiers: [blankTierRow()],
  multidropFreePoints: '3',
  multidropExtraRate: '0',
  catatan: '',
  berlakuMulai: todayIso(),
  berlakuSampai: '',
});

/** First failing rule as Indonesian copy, else null. Tipe-aware. */
export const validateConfigDraft = (d: ConfigDraft): string | null => {
  if (d.tipe === 'DISTANCE_TIER') {
    const err = validateTiers(d.distanceTiers);
    if (err) return err;
  }
  if (d.tipe === 'PER_TRIP_FLAT') {
    const err =
      validateBodyRows(d.ratesFlatPerBody) ??
      validateBodyRows(d.ratesFlatPerBodyMulti);
    if (err) return err;
  }
  const free = Number(d.multidropFreePoints.trim());
  if (
    d.multidropFreePoints.trim() === '' ||
    !Number.isInteger(free) ||
    free < 0
  ) {
    return v.multidropFreePoints;
  }
  if (d.berlakuSampai && d.berlakuMulai && d.berlakuSampai < d.berlakuMulai) {
    return v.berlakuRange;
  }
  return null;
};

const money = (s: string): string => s.trim() || '0';

export const toCreateTariffPayload = (
  d: ConfigDraft,
  clientId: number
): CreateTariffPayload => {
  const payload: CreateTariffPayload = {
    clientId,
    tipe: d.tipe,
    rateFlat: money(d.rateFlat),
    rateFlatMulti: money(d.rateFlatMulti),
    rateFixed: money(d.rateFixed),
    asuransi: money(d.asuransi),
    hargaReverse: money(d.hargaReverse),
    hariOperasi: d.hariOperasi,
    shiftOperasi: d.shiftOperasi,
    tagihPerRit: d.tagihPerRit,
    multidropFreePoints: Number(d.multidropFreePoints.trim()),
    multidropExtraRate: money(d.multidropExtraRate),
    catatan: d.catatan.trim(),
    berlakuMulai: d.berlakuMulai,
    berlakuSampai: d.berlakuSampai || null,
  };
  if (d.tipe === 'PER_TRIP_FLAT') {
    payload.ratesFlatPerBody = bodyRowsToMap(d.ratesFlatPerBody);
    payload.ratesFlatPerBodyMulti = bodyRowsToMap(d.ratesFlatPerBodyMulti);
    payload.driverShareFlat = money(d.shares.driver);
    payload.helperShareFlat = money(d.shares.helper);
    payload.driverShareFlatMulti = money(d.shares.driverMulti);
    payload.helperShareFlatMulti = money(d.shares.helperMulti);
  }
  if (d.tipe === 'DISTANCE_TIER') {
    payload.distanceTiers = tierRowsToPayload(d.distanceTiers);
  }
  return payload;
};

// ─────────────────────────────────────────────────────────────────────────────
// Ring draft
// ─────────────────────────────────────────────────────────────────────────────

export interface RingDraft {
  nama: string;
  urutan: string;
  ratePerTrip: string;
  ratePerTripMulti: string;
  ratesPerBody: BodyRateRow[];
  ratesPerBodyMulti: BodyRateRow[];
  shares: SharesDraft;
  keterangan: string;
}

export const blankRing = (): RingDraft => ({
  nama: '',
  urutan: '0',
  ratePerTrip: '0',
  ratePerTripMulti: '0',
  ratesPerBody: [],
  ratesPerBodyMulti: [],
  shares: blankShares(),
  keterangan: '',
});

export const validateRingDraft = (d: RingDraft): string | null =>
  validateBodyRows(d.ratesPerBody) ?? validateBodyRows(d.ratesPerBodyMulti);

export const toCreateRingPayload = (d: RingDraft): CreateRingPayload => ({
  nama: d.nama.trim(),
  urutan: Number(d.urutan) || 0,
  ratePerTrip: money(d.ratePerTrip),
  ratePerTripMulti: money(d.ratePerTripMulti),
  ratesPerBody: bodyRowsToMap(d.ratesPerBody),
  ratesPerBodyMulti: bodyRowsToMap(d.ratesPerBodyMulti),
  driverShare: money(d.shares.driver),
  helperShare: money(d.shares.helper),
  driverShareMulti: money(d.shares.driverMulti),
  helperShareMulti: money(d.shares.helperMulti),
  keterangan: d.keterangan.trim(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Read-side summaries (table + ring list)
// ─────────────────────────────────────────────────────────────────────────────

/** Number of per-body overrides (single + multi) on a config or ring. */
export const countOverrides = (
  row: Pick<TariffConfig, 'ratesFlatPerBody' | 'ratesFlatPerBodyMulti'> |
    Pick<TariffRing, 'ratesPerBody' | 'ratesPerBodyMulti'>
): number => {
  const a =
    'ratesPerBody' in row ? row.ratesPerBody : row.ratesFlatPerBody;
  const b =
    'ratesPerBodyMulti' in row
      ? row.ratesPerBodyMulti
      : row.ratesFlatPerBodyMulti;
  return Object.keys(a ?? {}).length + Object.keys(b ?? {}).length;
};

/** `true` when the config is limited to some days or shifts (CR4b-D7). */
export const hasOperatingWindow = (
  cfg: Pick<TariffConfig, 'hariOperasi' | 'shiftOperasi'>
): boolean =>
  (cfg.hariOperasi?.length ?? 0) > 0 || (cfg.shiftOperasi?.length ?? 0) > 0;
